import { useEffect, useRef, useState } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { useMutation } from "@tanstack/react-query";
import CustomPhoneNumber, { type Country } from "../components/custom-phone-number";
import { APIMapping } from "../constants/api-mapping.cnst";
import { fetchApi } from "../services/http.service";
import { IconCircleCheck, IconEdit, IconEyeFilled, IconEyeOff } from "@tabler/icons-react";
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';

// ================= HELPERS =================
const getInitialFormData = () => {
  const data = JSON.parse(localStorage.getItem("metaInfo") || "{}");

  let phoneNumber = "";
  let country = null;

  if (data.mobileNo) {
    try {
      const parsed = JSON.parse(data.mobileNo);
      phoneNumber = parsed?.phoneNumber ? parsed.phoneNumber : parsed;
      country = parsed.country;
    } catch { }
  }

  return {
    firstName: data.firstName || "",
    lastName: data.lastName || "",
    username: data.username || "",
    email: data.email || "",
    phoneNumber,
    country
  };
};

// ================= VALIDATION =================
const noSpaceRegex = /^[^\s]+$/;

const userSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, "First Name is required")
    .min(2, "First Name must be at least 2 characters long")
    .refine(val => noSpaceRegex.test(val), "No spaces allowed"),

  lastName: z
    .string()
    .trim()
    .min(1, "Last Name is required")
    .min(2, "Last Name must be at least 2 characters long")
    .refine(val => noSpaceRegex.test(val), "No spaces allowed"),

  username: z
    .string()
    .trim()
    .min(1, "Username is required")
    .min(2, "Username must be at least 2 characters long")
    .refine(val => noSpaceRegex.test(val), "No spaces allowed"),

  email: z.string().min(1, "Email is required").email("Invalid email"),

  phoneNumber: z
    .string()
    .min(1, "Phone number is required")
    .regex(/^\d{10}$/, "Invalid phone number"),

  country: z.any()
}).refine((data) => {
  if (data.country?.dial_code === "+91") {
    return /^[6-9]\d{9}$/.test(data.phoneNumber);
  }
  return true;
}, {
  message: "Invalid phone number",
  path: ["phoneNumber"]
});

const passwordSchema = z
  .object({
    newPassword: z
      .string()
      .min(8)
      .regex(/[A-Z]/)
      .regex(/[a-z]/)
      .regex(/\d.*\d/)
      .regex(/[!@#$%^&*]/),
    confirmPassword: z.string()
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
    params: { type: "custom" }
  });

// ================= API =================
const updateUserApi = async (data: any) =>
  fetchApi({ endpoint: APIMapping.UPDATE_USER, method: "PUT", body: data, pathParams: { user_id: JSON.parse(localStorage.getItem("metaInfo") || "{}").userId as string }, isUmUrl: true });

const updatePasswordApi = async (data: any) =>
  fetchApi({ endpoint: APIMapping.RESET_PASSWORD, method: "PUT", body: { ...data, password: data.newPassword, userId: JSON.parse(localStorage.getItem("metaInfo") || "{}").userId }, isUmUrl: true });

// ================= COMPONENT =================
export default function MyProfile() {
  const toast = useRef<Toast>(null);

  const [allowEdit, setAllowEdit] = useState(false);
  const [isPwdSubmitted, setIsPwdSubmitted] = useState(false);
  const [allowPwd, setAllowPwd] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [showPwd, setShowPwd] = useState({
    newPwd: false,
    confirmPwd: false
  });

  // ================= USER FORM =================
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    getValues,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(userSchema),
    mode: "onChange",
    reValidateMode: "onChange"
  });

  // ================= PASSWORD FORM =================
  const {
    register: pwdRegister,
    handleSubmit: handlePwdSubmit,
    watch: pwdWatch,
    reset: resetPwd,
    formState: { errors: pwdErrors, isDirty }
  } = useForm({
    resolver: zodResolver(passwordSchema),
    mode: "onChange"
  });

  // ================= INIT =================
  useEffect(() => {
    const initial = getInitialFormData();
    reset(initial);
    setSelectedCountry(initial.country);
  }, []);

  // ================= MUTATIONS =================
  const userMutation = useMutation({
    mutationFn: updateUserApi,
    onSuccess: () => {
      toast.current?.show({ severity: "success", summary: "Success", detail: "User Details updated successfully" });
      const metaInfo = JSON.parse(localStorage.getItem("metaInfo") || "{}");

      const values = getValues();

      metaInfo.firstName = values.firstName;
      metaInfo.lastName = values.lastName;
      metaInfo.username = values.username;
      metaInfo.email = values.email;

      metaInfo.mobileNo = JSON.stringify({
        country: values.country,
        phoneNumber: values.phoneNumber
      });

      localStorage.setItem("metaInfo", JSON.stringify(metaInfo));
      setAllowEdit(false);
    },
    onError: (err: any) => {
      toast.current?.clear();
      toast.current?.show({ severity: "error", summary: "Error", detail: err?.message ?? "Failed to update user details" });
    }
  });

  const pwdMutation = useMutation({
    mutationFn: updatePasswordApi,
    onSuccess: () => {
      toast.current?.show({ severity: "success", summary: "Success", detail: "Password updated successfully" });
      resetPwd({
        newPassword: "",
        confirmPassword: ""
      });
      setAllowPwd(false);
      setIsPwdSubmitted(false);
    },
    onError: (err: any) => {
      toast.current?.clear();
      toast.current?.show({ severity: "error", summary: "Error", detail: err?.message ?? "Failed to update password" });
    }
  });

  // ================= HANDLERS =================
  const onSubmit = (data: any) => {
    userMutation.mutate({
      metaInfo: {
        ...data,
        "customerId": JSON.parse(localStorage.getItem("metaInfo") || "{}")?.customerId,
        "customerName": JSON.parse(localStorage.getItem("metaInfo") || "{}")?.customerName,
        "status": true,
        mobileNo: JSON.stringify({
          country: selectedCountry,
          phoneNumber: data.phoneNumber
        })
      },
      "addRoles": {},
      "updateRoles": {},
      "deleteRoles": []
    });
  };

  const isAdmin = !!JSON.parse(localStorage.getItem("userPermissions") || '{}')?.['User Management'];

  const onPwdSubmit = (data: any) => {
    setIsPwdSubmitted(true);
    pwdMutation.mutate(data);
  };

  const newPwd = pwdWatch("newPassword") || "";
  const confirmPwd = pwdWatch("confirmPassword") || "";

  const userDetailsHeader = (

    <div className="flex justify-between items-center">
      <h3 className="m-0 font-semibold">User Details</h3>

      {!allowEdit && (
        <Button
          label="Edit"
          icon={<IconEdit size={20} />}
          // loading={isDownloading}
          // disabled={isDownloading || displayTableData.length === 0}
          className="bg-brand-600 border-brand-600 text-white dark:bg-brand-400 dark:border-brand-500"
          onClick={() => setAllowEdit(true)}
        />
      )}

    </div>
  )


  // ================= UI =================
  return (
    <>
      <Toast ref={toast} />
      <div className="p-4">

        {/* USER */}
        <Card
          header={userDetailsHeader}
        >
          <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-12 gap-4">


            {/* FIRST NAME */}
            <div className="col-span-12 md:col-span-4 flex flex-col gap-1">
              <label className="font-semibold">
                First Name <span className="text-red-500">*</span>
              </label>

              <InputText
                {...register("firstName")}
                disabled={!allowEdit || (allowEdit && !isAdmin)}
                className="w-full"
                onPaste={(e) => {
                  const paste = e.clipboardData.getData("text");
                  if (/[^A-Za-z]/.test(paste)) e.preventDefault();
                }}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^A-Za-z]/g, "");

                  setValue("firstName", value, {
                    shouldValidate: true,
                    shouldDirty: true
                  });
                }}
                placeholder="Enter First Name"
              />

              {errors.firstName && (
                <small className="text-red-500">{errors.firstName.message}</small>
              )}
            </div>

            {/* LAST NAME */}
            <div className="col-span-12 md:col-span-4 flex flex-col gap-1">
              <label className="font-semibold">
                Last Name <span className="text-red-500">*</span>
              </label>

              <InputText
                {...register("lastName")}
                disabled={!allowEdit || (allowEdit && !isAdmin)}
                className="w-full"
                onPaste={(e) => {
                  const paste = e.clipboardData.getData("text");
                  if (/[^A-Za-z]/.test(paste)) e.preventDefault();
                }}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^A-Za-z]/g, "");

                  setValue("lastName", value, {
                    shouldValidate: true,
                    shouldDirty: true
                  });
                }}
                placeholder="Enter Last Name"
              />

              {errors.lastName && (
                <small className="text-red-500">{errors.lastName.message}</small>
              )}
            </div>

            {/* USERNAME */}
            <div className="col-span-12 md:col-span-4 flex flex-col gap-1">
              <label className="font-semibold">
                Username <span className="text-red-500">*</span>
              </label>

              <InputText
                {...register("username")}
                disabled={!allowEdit}
                className="w-full"
                onPaste={(e) => {
                  const paste = e.clipboardData.getData("text");
                  if (/[^A-Za-z0-9]/.test(paste)) e.preventDefault();
                }}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^A-Za-z0-9]/g, "");

                  setValue("username", value, {
                    shouldValidate: true,
                    shouldDirty: true
                  });
                }}
                placeholder="Enter Username"
              />

              {errors.username && (
                <small className="text-red-500">{errors.username.message}</small>
              )}
            </div>

            {/* EMAIL */}
            <div className="col-span-12 md:col-span-4 flex flex-col gap-1">
              <label className="font-semibold">
                Email <span className="text-red-500">*</span>
              </label>

              <InputText
                {...register("email")}
                disabled={!allowEdit || (allowEdit && !isAdmin)}
                className="w-full"
                onChange={(e) => {
                  const value = e.target.value.replace(/\s/g, "");

                  setValue("email", value, {
                    shouldValidate: true,
                    shouldDirty: true
                  });
                }}
                placeholder="Enter Email"
              />

              {errors.email && (
                <small className="text-red-500">{errors.email.message}</small>
              )}
            </div>

            {/* PHONE */}
            <div className="col-span-12 md:col-span-4 flex flex-col gap-1">
              <label className="font-semibold">
                Phone Number <span className="text-red-500">*</span>
              </label>

              <CustomPhoneNumber
                value={watch("phoneNumber")}
                disabled={!allowEdit || (allowEdit && !isAdmin)}
                country={selectedCountry}
                onChange={(val: any) => setValue("phoneNumber", val, {
                  shouldValidate: true,
                  shouldDirty: true
                })}
                onCountryChange={(val: any) => {
                  setSelectedCountry(val);
                  setValue("country", val, {
                    shouldValidate: true,
                    shouldDirty: true
                  });
                }}
                error={errors.phoneNumber?.message as string}
              />
            </div>

            {/* BUTTONS */}
            {allowEdit && (
              <div className="col-span-12 flex justify-end gap-2">
                <Button
                  label="Cancel"
                  className="bg-gray-200 border-gray-200 text-gray-700 dark:bg-gray-800 dark:border-gray-800 dark:text-gray-200"
                  onClick={() => {
                    const initial = getInitialFormData();
                    reset(initial);
                    setSelectedCountry(initial.country);
                    setAllowEdit(false);
                  }}
                />
                <Button label="Update" type="submit" loading={userMutation.isPending} disabled={!!Object.keys(errors).length}
                  className="bg-brand-600 border-brand-600 text-white dark:bg-brand-400 dark:border-brand-500" />
              </div>
            )}
          </form>
        </Card>

        {/* PASSWORD */}
        <Card title="Password" className="mt-4">
          <form onSubmit={(e) => {
            if (!allowPwd) {
              e.preventDefault();
              setAllowPwd(true);
              return;
            }

            handlePwdSubmit(onPwdSubmit)(e);
          }}>

            {allowPwd && (
              <>
                <p className="mb-3 mt-2">
                  Please choose a password not used before. Must be 8–14 characters including:
                </p>
                {/* RULES */}
                <ul className="mb-3 text-sm space-y-1">

                  {/* UPPERCASE */}
                  <li className="flex items-center gap-2">
                    <IconCircleCheck
                      size={16}
                      className={
                        newPwd.length > 0
                          ? /[A-Z]/.test(newPwd)
                            ? "text-green-500"
                            : "text-red-500"
                          : "text-gray-400"
                      }
                    />
                    <span
                      className={
                        newPwd.length > 0
                          ? /[A-Z]/.test(newPwd)
                            ? "text-green-500"
                            : "text-red-500"
                          : ""
                      }
                    >
                      1 - Uppercase
                    </span>
                  </li>

                  {/* LOWERCASE */}
                  <li className="flex items-center gap-2">
                    <IconCircleCheck
                      size={16}
                      className={
                        newPwd.length > 0
                          ? /[a-z]/.test(newPwd)
                            ? "text-green-500"
                            : "text-red-500"
                          : "text-gray-400"
                      }
                    />
                    <span
                      className={
                        newPwd.length > 0
                          ? /[a-z]/.test(newPwd)
                            ? "text-green-500"
                            : "text-red-500"
                          : ""
                      }
                    >
                      1 - Lowercase
                    </span>
                  </li>

                  {/* NUMERIC */}
                  <li className="flex items-center gap-2">
                    <IconCircleCheck
                      size={16}
                      className={
                        newPwd.length > 0
                          ? /\d.*\d/.test(newPwd)
                            ? "text-green-500"
                            : "text-red-500"
                          : "text-gray-400"
                      }
                    />
                    <span
                      className={
                        newPwd.length > 0
                          ? /\d.*\d/.test(newPwd)
                            ? "text-green-500"
                            : "text-red-500"
                          : ""
                      }
                    >
                      2 - Numeric
                    </span>
                  </li>

                  {/* SPECIAL */}
                  <li className="flex items-center gap-2">
                    <IconCircleCheck
                      size={16}
                      className={
                        newPwd.length > 0
                          ? /[!@#$%^&*]/.test(newPwd)
                            ? "text-green-500"
                            : "text-red-500"
                          : "text-gray-400"
                      }
                    />
                    <span
                      className={
                        newPwd.length > 0
                          ? /[!@#$%^&*]/.test(newPwd)
                            ? "text-green-500"
                            : "text-red-500"
                          : ""
                      }
                    >
                      1 - Special Character
                    </span>
                  </li>

                </ul>

                <div className="grid grid-cols-12 gap-4">

                  {/* NEW PASSWORD */}
                  <div className="col-span-12 md:col-span-4 flex flex-col gap-1">
                    <label className="font-semibold">
                      New Password <span className="text-red-500">*</span>
                    </label>

                    <IconField iconPosition="right">

                      {/* INPUT */}
                      <InputText
                        type={showPwd.newPwd ? "text" : "password"}
                        {...pwdRegister("newPassword")}
                        onChange={(e) => {
                          setIsPwdSubmitted(false); // reset when user edits
                          pwdRegister("newPassword").onChange(e);
                        }}
                        className={`w-full ${newPwd.length > 0 && pwdErrors.newPassword ? "p-invalid" : ""
                          }`}
                        placeholder="New Password"
                      />
                      <InputIcon
                        className="cursor-pointer text-gray-500 hover:text-gray-700" onClick={() =>
                          setShowPwd((prev) => ({ ...prev, newPwd: !prev.newPwd }))
                        }>
                        {showPwd.newPwd ? <IconEyeOff size={18} /> : <IconEyeFilled size={18} />}
                      </InputIcon>
                    </IconField>
                  </div>

                  {/* CONFIRM PASSWORD */}
                  <div className="col-span-12 md:col-span-4 flex flex-col gap-1">
                    <label className="font-semibold">
                      Confirm Password <span className="text-red-500">*</span>
                    </label>

                    <IconField iconPosition="right">

                      {/* INPUT */}
                      <InputText
                        type={showPwd.confirmPwd ? "text" : "password"}
                        {...pwdRegister("confirmPassword")}
                        onChange={(e) => {
                          setIsPwdSubmitted(false); // reset when user edits
                          pwdRegister("confirmPassword").onChange(e);
                        }}
                        disabled={!newPwd || newPwd.length === 0}
                        className={`w-full ${pwdErrors.confirmPassword ? "p-invalid" : ""
                          }`}
                        placeholder="Confirm Password"
                      />
                      <InputIcon
                        className="cursor-pointer text-gray-500 hover:text-gray-700"
                        onClick={() =>
                          setShowPwd((prev) => ({
                            ...prev,
                            confirmPwd: !prev.confirmPwd
                          }))
                        }>
                        {showPwd.confirmPwd ? <IconEyeOff size={18} /> : <IconEyeFilled size={18} />}
                      </InputIcon>

                    </IconField>

                    {/* ERRORS */}
                    {pwdErrors.confirmPassword?.type === "required" && newPwd.length > 0 && (
                      <small className="text-red-500">Confirm Password is required</small>
                    )}

                    {pwdErrors.confirmPassword &&
                      pwdErrors.confirmPassword.type !== "required" && (
                        <small className="text-red-500">Passwords do not match</small>
                      )}
                  </div>

                </div>
              </>
            )}

            {/* FOOTER */}
            <div className="col-span-12 flex items-center mt-3 gap-4 justify-end">
              {!allowPwd && (
                <div className="flex-auto">
                  Keep your account secure by regularly updating your password.
                </div>
              )}

              {allowPwd && (<Button
                label="Cancel"
                className="bg-gray-200 border-gray-200 text-gray-700 dark:bg-gray-800 dark:border-gray-800 dark:text-gray-200"
                onClick={() => {
                  setAllowPwd(false);

                  resetPwd({
                    newPassword: "",
                    confirmPassword: ""
                  });
                }}
              />
              )}

              <Button
                label="Update Password"
                type="submit"
                className="bg-brand-600 border-brand-600 text-white dark:bg-brand-400 dark:border-brand-500"
                loading={pwdMutation.isPending}
                disabled={
                  allowPwd && (!newPwd ||                     // empty new password
                    !confirmPwd ||                 // empty confirm password
                    !!pwdErrors.newPassword ||     // validation error
                    !!pwdErrors.confirmPassword || // mismatch / required
                    !isDirty ||                    // no change
                    pwdMutation.isPending ||
                    isPwdSubmitted)         // prevent multiple clicks
                }
              />
            </div>

          </form>
        </Card>

      </div>
    </>
  );
}